import type { StepOutputPayload, StructuredOutput, ThreadEntry, ToolCallPayload, ToolTurnFinishedPayload, ToolTurnRequestedPayload } from "../../../api/threads/liveMessages";
import { agentMemorySave } from "../../memories/agentSave";
import { reviewedInputOf } from "./humanStepResponse";

/**
 * The log as the reader sees it (design review item 3): one block per tool step instead of
 * its request, each output and its result. Entries stay in log order (threads.md T-1): a
 * step's entries form one block only while nothing else comes between them. Pure, so the
 * grouping is tested in one place. Consecutive agent calls form their own compact block.
 */

/** A tool step's entries, from its request; outputs and result as they arrive. */
export interface ToolStepBlock {
  readonly kind: "toolStep";
  readonly turnId: string;
  readonly request: ThreadEntry & { readonly payload: ToolTurnRequestedPayload };
  readonly outputs: readonly (ThreadEntry & { readonly payload: StepOutputPayload })[];
  /** `null` while the step runs. */
  readonly finished: (ThreadEntry & { readonly payload: ToolTurnFinishedPayload }) | null;
}

export interface ToolCallsBlock {
  readonly kind: "toolCalls";
  readonly turnId: string;
  readonly calls: readonly (ThreadEntry & { readonly payload: ToolCallPayload })[];
}

export type LogBlock = { readonly kind: "entry"; readonly entry: ThreadEntry } | ToolStepBlock | ToolCallsBlock;

export interface ToolCallGroupStats {
  readonly milliseconds: number;
  readonly count: number;
  readonly failed: number;
}

/** Consecutive calls span their recorded start and latest end (threads.md T-1). */
export function toolCallGroupStats(calls: ToolCallsBlock["calls"]): ToolCallGroupStats {
  const first = calls[0];
  const start = first === undefined ? 0 : Date.parse(first.payload.started_at);
  const end = calls.reduce((latest, call) => Math.max(latest, Date.parse(call.payload.started_at) + call.payload.duration_milliseconds), start);
  return { milliseconds: Math.max(0, end - start), count: calls.length, failed: calls.filter((call) => call.payload.outcome !== "ok").length };
}

/** The step a turn ran, from its request, so its outputs can be named. */
export interface TurnStep {
  readonly stepKey: string | null;
  readonly step: string | null;
}

/** An agent's turn (not a tool's), from its request: whose request it answered (F70, R2). */
export interface AgentTurn {
  /** The participant who asked; `null` for the runner's own step turns and older replies. */
  readonly requestedBy: string | null;
}

/** Whose agent answered, when it is another person's: one's own agent is just "Agent". */
export function otherPersonsAgent(turn: AgentTurn | null, viewer: string | null): string | null {
  const asker = turn?.requestedBy ?? null;
  return asker === viewer ? null : asker;
}

export interface LogView {
  readonly blocks: readonly LogBlock[];
  readonly turnSteps: ReadonlyMap<string, TurnStep>;
  readonly agentTurns: ReadonlyMap<string, AgentTurn>;
  /** Each loaded review request's reviewed result, by its entry number: what an answer is compared with. */
  readonly reviewed: ReadonlyMap<number, StructuredOutput>;
}

export function logView(entries: readonly ThreadEntry[]): LogView {
  const blocks: LogBlock[] = [];
  const turnSteps = new Map<string, TurnStep>();
  const agentTurns = new Map<string, AgentTurn>();
  const reviewed = new Map<number, StructuredOutput>();
  for (const entry of entries) {
    const payload = entry.payload;
    const reviewedValue = payload.kind === "human_step_requested" ? reviewedInputOf(payload)?.value : null;
    if (reviewedValue != null) {
      reviewed.set(entry.number, reviewedValue);
    }
    if ((payload.kind === "turn_requested" || payload.kind === "tool_turn_requested") && payload.step_key !== null) {
      turnSteps.set(payload.turn_id, { stepKey: payload.step_key, step: payload.step });
    }
    if (payload.kind === "turn_requested") {
      agentTurns.set(payload.turn_id, { requestedBy: payload.requested_by });
    }
    const last = blocks.at(-1);
    const open = last?.kind === "toolStep" && last.finished === null ? last : null;
    if (payload.kind === "tool_call" && agentMemorySave(payload) === null) {
      const call = { ...entry, payload };
      if (last?.kind === "toolCalls" && last.turnId === payload.turn_id) {
        blocks[blocks.length - 1] = { ...last, calls: [...last.calls, call] };
      } else {
        blocks.push({ kind: "toolCalls", turnId: payload.turn_id, calls: [call] });
      }
    } else if (payload.kind === "tool_turn_requested") {
      blocks.push({ kind: "toolStep", turnId: payload.turn_id, request: { ...entry, payload }, outputs: [], finished: null });
    } else if (open !== null && payload.kind === "step_output" && payload.turn_id === open.turnId) {
      blocks[blocks.length - 1] = { ...open, outputs: [...open.outputs, { ...entry, payload }] };
    } else if (open !== null && payload.kind === "tool_turn_finished" && payload.turn_id === open.turnId) {
      blocks[blocks.length - 1] = { ...open, finished: { ...entry, payload } };
    } else {
      blocks.push({ kind: "entry", entry });
    }
  }
  return { blocks, turnSteps, agentTurns, reviewed };
}

/** Every entry of a block, in log order. */
export function entriesOfBlock(block: ToolStepBlock | ToolCallsBlock): readonly ThreadEntry[] {
  if (block.kind === "toolCalls") return block.calls;
  return [block.request, ...block.outputs, ...(block.finished === null ? [] : [block.finished])];
}
